import { useCallback, useEffect, useState } from 'react';

import { buildDefaultFileName, validateFileName } from '@/utils/downloadFilename';

export interface UseDownloadFileName {
    fileName: string;
    setFileName: (value: string) => void;
    /** Validation error to display, or null. Suppressed until the field has been edited so a fresh
     *  empty (required) field does not open showing a red error. */
    error: string | null;
    /** Whether the current name passes validation, regardless of whether it has been edited. */
    isValid: boolean;
}

export interface UseDownloadFileNameOptions {
    /**
     * When true (default), the field is seeded with a client-derived default ("<prefix> - <client>")
     * and re-seeded whenever the active client changes. When false, the field starts empty and is
     * never auto-filled, so the user must enter a name.
     */
    prefill?: boolean;
}

/**
 * Manages a download Filename field: optionally seeds it with a client-derived default, validates
 * it, and reports whether it is currently valid. Shared by the POI and high-risk air-valve download
 * panels.
 */
export function useDownloadFileName(
    prefix: string,
    clientName: string | undefined,
    { prefill = true }: UseDownloadFileNameOptions = {},
): UseDownloadFileName {
    const [name, setName] = useState(() => (prefill ? buildDefaultFileName(prefix, clientName) : ''));
    const [touched, setTouched] = useState(false);

    const setFileName = useCallback((value: string) => {
        setTouched(true);
        setName(value);
    }, []);

    // Re-seed with the client-derived default when the active client (or prefix) changes, so
    // switching clients always offers a fresh, correct default. Skipped when prefill is off.
    useEffect(() => {
        if (!prefill) return;
        // eslint-disable-next-line react-hooks/set-state-in-effect, react-x/set-state-in-effect
        setName(buildDefaultFileName(prefix, clientName));
    }, [prefix, clientName, prefill]);

    const validationError = validateFileName(name);
    return {
        fileName: name,
        setFileName,
        error: touched ? validationError : null,
        isValid: validationError == null,
    };
}
